import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Collapse,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Inventory as InventoryIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Print as PrintIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { StockEntryNew } from '../types/stockEntryNew';
import { Article } from '../types';
import { Supplier } from '../types/supplier';
import { stockEntriesNewAPI, articlesAPI, suppliersAPI, auditLogsAPI } from '../services/api';
import {
  useStockEntriesList,
  useDeletedStockEntries,
  useInvalidateStockEntries,
} from '../hooks/queries/useStockEntries';
import { useArticlesDropdown, useSuppliersDropdown } from '../hooks/queries/useDropdowns';
import AlertDialog from './AlertDialog';
import useAlert from '../hooks/useAlert';
import { getModuleTableTheme } from '../theme/tableTheme';
import { extractErrorMessage } from '../utils/errorHandler';
import usePdfPreview from '../hooks/usePdfPreview';
import ConfirmationDialog, { ConfirmationField } from './ConfirmationDialog';
import StockEntryNewAdd from './StockEntryNewAdd';
import StockEntryNewEdit from './StockEntryNewEdit';
import CustomTablePagination from './CustomTablePagination';
import PermissionButton from './PermissionButton';
import TruncatedTextWithTooltip from './TruncatedTextWithTooltip';
import ColumnFilter from './ColumnFilter';
import { useColumnFilters } from '../hooks/useColumnFilters';
import Button from '@mui/material/Button';

const STOCK_COLOR = '#2196F3';

const ENTRY_FILTER_SCHEMA = {
  bl_number:   { type: 'text', param: 'bl_number' },
  date:        { type: 'dateRange', paramFrom: 'start_date', paramTo: 'end_date' },
  supplier_id: { type: 'autocomplete', param: 'supplier_id' },
} as const;
// generateStockEntryPDF is dynamically imported in the print handlers so the
// jspdf chunk only loads when the user clicks Print.

const StockEntryNewManagement: React.FC = () => {
  const { data: articles = [] } = useArticlesDropdown();
  const { data: suppliers = [] } = useSuppliersDropdown();
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState<StockEntryNew | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<StockEntryNew | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const { filters, update, reset, hasActive, toQueryParams } = useColumnFilters(ENTRY_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });
  const supplierOptions = suppliers.map((s) => ({ value: String(s.id), label: s.name }));

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cached + live list: auto-refreshes when any user changes a stock entry
  // (WebSocket invalidation), and revisiting the same page/filters is instant.
  const listParams = useMemo(() => {
    const extra = toQueryParams();
    return {
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      search: debouncedSearch || undefined,
      extra: Object.keys(extra).length > 0 ? extra : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filters]);

  const entriesQuery = useStockEntriesList(listParams, tabValue === 0);
  const deletedQuery = useDeletedStockEntries(tabValue === 1);
  const invalidateEntries = useInvalidateStockEntries();

  const stockEntries = entriesQuery.data?.items ?? [];
  const totalCount = entriesQuery.data?.total ?? 0;
  const deletedStockEntries = deletedQuery.data ?? [];
  const loading = tabValue === 0 ? entriesQuery.isLoading : deletedQuery.isLoading;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    setExpandedRows(new Set());
  };

  const handleRestore = async (entry: StockEntryNew) => {
    if (!entry.id) return;
    showConfirm(
      `Etes-vous sur de vouloir restaurer l'entree de stock BL "${entry.bl_number}" ?`,
      async () => {
        try {
          await stockEntriesNewAPI.restore(entry.id!);
          showSuccess('Entree de stock restauree avec succes');
          invalidateEntries();
        } catch (err) {
          showError("Erreur lors de la restauration de l'entree");
        }
      },
      {
        title: 'Confirmer la restauration',
        confirmText: 'Restaurer',
        cancelText: 'Annuler'
      }
    );
  };

  const formatNumber = (value: any, decimals: number = 2): string => {
    return Number(value || 0).toFixed(decimals);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'MAD'
    }).format(amount);
  };

  const handleOpenAddDialog = () => {
    setFormMinimized(false);
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (entry: StockEntryNew) => {
    setEditingEntry(entry);
    setOpenEditDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setFormMinimized(false);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingEntry(null);
  };

  const handleDeleteClick = (entry: StockEntryNew) => {
    setEntryToDelete(entry);
    setShowDeleteConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!entryToDelete || !entryToDelete.id) return;

    setIsDeleting(true);
    try {
      await stockEntriesNewAPI.delete(entryToDelete.id);
      showSuccess(`BL supprime avec succes (${entryToDelete.items?.length || 0} article(s))`);
      setShowDeleteConfirmDialog(false);
      setEntryToDelete(null);
      invalidateEntries();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const getDeleteConfirmationFields = (): ConfirmationField[] => {
    if (!entryToDelete) return [];

    const fields: ConfirmationField[] = [];

    fields.push({ label: 'Date', value: entryToDelete.date ? new Date(entryToDelete.date).toLocaleDateString('fr-FR') : '-' });
    fields.push({ label: 'BL N°', value: entryToDelete.bl_number });

    if (entryToDelete.supplier_name) {
      fields.push({ label: 'Fournisseur', value: entryToDelete.supplier_name });
    }

    fields.push({ label: '---', value: '---' });
    fields.push({ label: 'Articles', value: `${entryToDelete.items?.length || 0}`, bold: true });

    // Add all articles
    (entryToDelete.items || []).forEach((item, index) => {
      fields.push({
        label: `Article ${index + 1}`,
        value: `${item.article_reference} - ${item.article_designation}`,
      });
      fields.push({
        label: `  Quantite`,
        value: `${formatNumber(item.quantity, 2)}`,
      });
      fields.push({
        label: `  Prix Unitaire`,
        value: `${formatNumber(item.unit_price)} MAD`,
      });
      fields.push({
        label: `  Total`,
        value: `${formatNumber(item.total)} MAD`,
      });
    });

    // Grand total
    fields.push({ label: '---', value: '---' });
    fields.push({
      label: 'TOTAL GENERAL',
      value: `${formatNumber(entryToDelete.grand_total)} MAD`,
      bold: true,
    });

    return fields;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    setExpandedRows(new Set());
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRows(new Set());
  };

  const toggleRowExpansion = (entryId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InventoryIcon /> Gestion des Entrées de Stock
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gérer les entrées de marchandises des fournisseurs
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {tabValue === 0 && hasActive() && (
              <Button
                size="small"
                startIcon={<FilterListOffIcon />}
                onClick={reset}
                sx={{ color: 'text.secondary' }}
              >
                Effacer les filtres
              </Button>
            )}
            {tabValue === 0 && (
              <PermissionButton
                moduleName="Stock"
                operation="add"
                miniModuleName="Stock Entry"
                variant="contained"
                color="primary"
                onClick={handleOpenAddDialog}
                startIcon={<AddIcon />}
              >
                Nouvelle Entrée
              </PermissionButton>
            )}
          </Stack>
        </Stack>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={`Entrées Actives (${totalCount})`} />
            <Tab label="Corbeille" />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <>
            <Box sx={{ mb: 2 }}>
              <TextField
                variant="outlined"
                placeholder="Rechercher par BL N° ou article..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{ width: 350 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <TableContainer sx={getModuleTableTheme('stock')}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap', width: 50 }}></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Date
                        <ColumnFilter type="dateRange" value={filters.date} onChange={(v) => update('date', v)} label="date" color={STOCK_COLOR} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        BL N°
                        <ColumnFilter type="text" value={filters.bl_number} onChange={(v) => update('bl_number', v)} label="BL N°" color={STOCK_COLOR} />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Fournisseur
                        <ColumnFilter type="autocomplete" value={filters.supplier_id} onChange={(v) => update('supplier_id', v)} options={supplierOptions} label="fournisseur" color={STOCK_COLOR} />
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Articles</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Total</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Chargement...
                      </TableCell>
                    </TableRow>
                  ) : stockEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        Aucune entrée de stock trouvée
                      </TableCell>
                    </TableRow>
                  ) : (
                    stockEntries.map((stockEntry) => {
                      const isExpanded = expandedRows.has(stockEntry.id!);
                      return (
                        <React.Fragment key={stockEntry.id}>
                          <TableRow
                            hover
                            sx={{
                              cursor: 'pointer',
                              '& > *': { borderBottom: isExpanded ? 'unset' : undefined },
                            }}
                            onClick={() => toggleRowExpansion(stockEntry.id!)}
                          >
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <IconButton size="small">
                                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {stockEntry.date ? new Date(stockEntry.date).toLocaleDateString('fr-FR') : '-'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Chip label={stockEntry.bl_number} size="small" color="primary" />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 200 }}>
                              <TruncatedTextWithTooltip text={stockEntry.supplier_name} maxWidth={200} />
                            </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                              <Chip label={stockEntry.items?.length || 0} size="small" color="info" />
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <strong>{formatCurrency(Number(stockEntry.grand_total))}</strong>
                            </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                              <Stack direction="row" spacing={0} justifyContent="center">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    openPreview({
                                      getBlob: async () => {
                                        const { generateStockEntryPDF } = await import('../utils/pdfGenerator');
                                        return (await generateStockEntryPDF(stockEntry, true)) as Blob;
                                      },
                                      fileName: `${stockEntry.bl_number}.pdf`,
                                    });
                                    auditLogsAPI.logPdfDownload('StockEntry', stockEntry.id || 0, `Aperçu PDF - ${stockEntry.bl_number}`);
                                  }}
                                  sx={{ color: '#1976d2' }}
                                  title="Imprimer"
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                                <PermissionButton
                                  moduleName="Stock"
                                  operation="edit"
                                  miniModuleName="Stock Entry"
                                  component="IconButton"
                                  size="small"
                                  color="primary"
                                  onClick={() => handleOpenEditDialog(stockEntry)}
                                  title="Modifier"
                                >
                                  <EditIcon fontSize="small" />
                                </PermissionButton>
                                <PermissionButton
                                  moduleName="Stock"
                                  operation="delete"
                                  miniModuleName="Stock Entry"
                                  component="IconButton"
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(stockEntry)}
                                  title="Supprimer"
                                >
                                  <DeleteIcon fontSize="small" />
                                </PermissionButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 'bold' }}>
                                    Détails des articles
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ backgroundColor: 'background.default' }}>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Référence</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Désignation</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Catégorie</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Unité</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Quantité</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Prix Unit.</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Total</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(stockEntry.items || []).map((item, index) => (
                                        <TableRow key={item.id || index}>
                                          <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 150 }}>
                                            <TruncatedTextWithTooltip text={item.article_reference} maxWidth={150} />
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 200 }}>
                                            <TruncatedTextWithTooltip text={item.article_designation} maxWidth={200} />
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {item.category && <Chip label={item.category} size="small" variant="outlined" />}
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.unit || '-'}</TableCell>
                                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatNumber(item.quantity, 2)}</TableCell>
                                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(Number(item.unit_price))}</TableCell>
                                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <strong>{formatCurrency(Number(item.total))}</strong>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomTablePagination
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}

        {tabValue === 1 && (
          <>
            <TableContainer sx={getModuleTableTheme('stock')}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap', width: 50 }}></TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>BL N°</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Fournisseur</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Articles</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Total</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Date de Suppression</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        Chargement...
                      </TableCell>
                    </TableRow>
                  ) : deletedStockEntries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        Aucune entrée de stock supprimée
                      </TableCell>
                    </TableRow>
                  ) : (
                    deletedStockEntries.map((stockEntry) => {
                      const isExpanded = expandedRows.has(stockEntry.id!);
                      return (
                        <React.Fragment key={stockEntry.id}>
                          <TableRow
                            hover
                            sx={{
                              cursor: 'pointer',
                              '& > *': { borderBottom: isExpanded ? 'unset' : undefined },
                            }}
                            onClick={() => toggleRowExpansion(stockEntry.id!)}
                          >
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <IconButton size="small">
                                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {stockEntry.date ? new Date(stockEntry.date).toLocaleDateString('fr-FR') : '-'}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Chip label={stockEntry.bl_number} size="small" color="primary" />
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 200 }}>
                              <TruncatedTextWithTooltip text={stockEntry.supplier_name} maxWidth={200} />
                            </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                              <Chip label={stockEntry.items?.length || 0} size="small" color="info" />
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <strong>{formatCurrency(Number(stockEntry.grand_total))}</strong>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {stockEntry.deleted_at ? new Date(stockEntry.deleted_at).toLocaleDateString('fr-FR') : '-'}
                            </TableCell>
                            <TableCell align="center" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                              <Stack direction="row" spacing={0} justifyContent="center">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    openPreview({
                                      getBlob: async () => {
                                        const { generateStockEntryPDF } = await import('../utils/pdfGenerator');
                                        return (await generateStockEntryPDF(stockEntry, true)) as Blob;
                                      },
                                      fileName: `${stockEntry.bl_number}.pdf`,
                                    });
                                    auditLogsAPI.logPdfDownload('StockEntry', stockEntry.id || 0, `Aperçu PDF - ${stockEntry.bl_number}`);
                                  }}
                                  sx={{ color: '#1976d2' }}
                                  title="Imprimer"
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  onClick={() => handleRestore(stockEntry)}
                                  color="primary"
                                  size="small"
                                  title="Restaurer"
                                >
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ margin: 2 }}>
                                  <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 'bold' }}>
                                    Détails des articles
                                  </Typography>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ backgroundColor: 'background.default' }}>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Référence</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Désignation</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Catégorie</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Unité</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Quantité</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Prix Unit.</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Total</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(stockEntry.items || []).map((item, index) => (
                                        <TableRow key={item.id || index}>
                                          <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 150 }}>
                                            <TruncatedTextWithTooltip text={item.article_reference} maxWidth={150} />
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 200 }}>
                                            <TruncatedTextWithTooltip text={item.article_designation} maxWidth={200} />
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {item.category && <Chip label={item.category} size="small" variant="outlined" />}
                                          </TableCell>
                                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.unit || '-'}</TableCell>
                                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatNumber(item.quantity, 2)}</TableCell>
                                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(Number(item.unit_price))}</TableCell>
                                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            <strong>{formatCurrency(Number(item.total))}</strong>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <CustomTablePagination
              count={deletedStockEntries.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Add Dialog */}
      <StockEntryNewAdd
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
        onSuccess={invalidateEntries}
        articles={articles}
        suppliers={suppliers}
        showError={showError}
        showSuccess={showSuccess}
      />

      {/* Edit Dialog */}
      <StockEntryNewEdit
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        onSuccess={invalidateEntries}
        stockEntry={editingEntry}
        articles={articles}
        suppliers={suppliers}
        showError={showError}
        showSuccess={showSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={showDeleteConfirmDialog}
        title="Confirmer la suppression"
        message="Etes-vous sur de vouloir supprimer ce BL ?"
        fields={getDeleteConfirmationFields()}
        movementType="entry"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteConfirmDialog(false);
          setEntryToDelete(null);
        }}
        confirmText="Supprimer"
        cancelText="Annuler"
        loading={isDeleting}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default StockEntryNewManagement;
